"use client";

import React, { useEffect, useRef, useState } from "react";
import { Workflow, Cpu, Database, TrendingUp, Settings } from "lucide-react";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const modules = [{
  id: 1,
  title: "CRM Synchronization",
  description: "The heartbeat of your operation. We fuse your lead flow directly into ServiceTitan or Housecall Pro, ensuring that every customer interaction is logged, tracked, and ready for dispatch with zero manual entry.",
  iconName: "Database",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/crm_sync_gear.png"
}, {
  id: 2,
  title: "Automated Triaging",
  description: "Precision sorting for high-stakes calls. Our engine filters emergencies from general inquiries, prioritizing high-value plumbing or HVAC jobs so your team focuses on revenue-generating labor, not administrative noise.",
  iconName: "Workflow",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/automated_funnel_sort.png"
}, {
  id: 3,
  title: "Voice Intelligence",
  description: "24/7 AI-powered response systems. Our voice agents handle after-hours calls with human-grade empathy and technical accuracy, booking emergency jobs while your competitors' phones go to voicemail.",
  iconName: "Cpu",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/voice_blueprint_wave.png"
}, {
  id: 4,
  title: "Emergency Dispatch",
  description: "Instant response protocols for critical failures. When a pipe bursts at 2 AM, our system doesn't just take a message—it initiates the recovery sequence and alerts your on-call technicians in real-time.",
  iconName: "TrendingUp",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/emergency_dispatch_hub.png"
}, {
  id: 5,
  title: "System Auditing",
  description: "Zero-leak operational oversight. We provide a transparent blueprint of your entire lead-to-job lifecycle, identifying and repairing every point of revenue friction within your sales and service funnel.",
  iconName: "Settings",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/system_audit_blueprint.png"
}];
const integrations = [{
  moduleId: 1,
  name: "ServiceTitan"
}, {
  moduleId: 1,
  name: "Housecall Pro"
}, {
  moduleId: 1,
  name: "Jobber"
}, {
  moduleId: 1,
  name: "Zapier"
}, {
  moduleId: 2,
  name: "Lead Filters"
}, {
  moduleId: 2,
  name: "Priority Routing"
}, {
  moduleId: 2,
  name: "SMS Alerts"
}, {
  moduleId: 2,
  name: "Airtable"
}, {
  moduleId: 3,
  name: "AI Voice"
}, {
  moduleId: 3,
  name: "Twilio"
}, {
  moduleId: 3,
  name: "Natural Language"
}, {
  moduleId: 3,
  name: "Speech-to-Text"
}, {
  moduleId: 4,
  name: "Mobile App"
}, {
  moduleId: 4,
  name: "Push Notifications"
}, {
  moduleId: 4,
  name: "GPS Sync"
}, {
  moduleId: 4,
  name: "On-Call Rota"
}, {
  moduleId: 5,
  name: "Performance Dash"
}, {
  moduleId: 5,
  name: "Revenue Tracking"
}, {
  moduleId: 5,
  name: "Lead Audit"
}, {
  moduleId: 5,
  name: "ROI Calc"
}];
const iconMap: Record<string, React.ElementType> = {
  Workflow,
  Cpu,
  Database,
  TrendingUp,
  Settings
};
function ModuleRow({
  module,
  moduleIntegrations,
  isEven,
  index
}: {
  module: (typeof modules)[0];
  moduleIntegrations: typeof integrations;
  isEven: boolean;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const IconComponent = iconMap[module.iconName];
  return <div data-section-id="143854" data-index={index} className={`py-24 lg:py-32 ${isEven ? "bg-transparent" : "bg-primary/5"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={`flex flex-col gap-12 lg:gap-24 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} ${isEven ? "lg:flex-row" : "lg:flex-row-reverse"}`}>
          {}
          <div className="w-full lg:w-1/2">
            <div className="relative aspect-[4/3] lg:aspect-square overflow-hidden rounded-none border border-primary/20 bg-muted/10 shadow-2xl shadow-black/80">
              <div className="absolute inset-0 bg-gradient-to-tr from-background/40 to-transparent z-10 mix-blend-overlay" />
              <img src={module.image} alt={module.title} className="object-cover w-full h-full transition-transform duration-700 hover:scale-105" loading="lazy" />
            </div>
          </div>

          {}
          <div className="w-full lg:w-1/2 space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center justify-center p-4 rounded-none bg-primary/10 border border-primary/30 text-primary shadow-[inset_0_2px_4px_rgba(0,0,0,0.3)]">
                {IconComponent && <IconComponent className="w-8 h-8" />}
              </div>
              
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-black tracking-tighter text-primary uppercase">
                  {module.title}
                </h2>
                <p className="text-lg md:text-xl text-foreground/80 font-sans leading-relaxed max-w-2xl">
                  {module.description}
                </p>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-primary/20">
              <h4 className="text-sm font-mono font-bold tracking-widest text-primary/70 uppercase">
                Core Integrations
              </h4>
              <div className="flex flex-wrap gap-2.5">
                {moduleIntegrations.map((integration, i) => <Badge key={i} data-index={i} variant="outline" className="px-3 py-1.5 text-sm rounded-none border-primary/40 text-foreground/70 bg-background/50 backdrop-blur-sm hover:bg-primary hover:text-primary-foreground transition-all duration-300">
                    {integration.name}
                  </Badge>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export default function OurCapabilities() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="our-recovery-engine" className="dark bg-background text-foreground w-full overflow-hidden">
      {}
      <div className="container mx-auto px-4 md:px-6 pt-24 lg:pt-32 pb-12">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center space-y-6 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-sm font-mono font-bold tracking-widest text-primary uppercase">
            Industrial Infrastructure
          </h2>
          <h3 className="text-4xl md:text-5xl lg:text-7xl font-serif font-black tracking-tighter text-foreground uppercase">
            The Recovery Engine
          </h3>
          <p className="text-xl text-muted-foreground font-sans max-w-2xl mx-auto">
            Deep dive into our specific AI-powered workflow and operational frameworks designed to scale your home service agency.
          </p>
        </div>
      </div>

      {}
      <div className="flex flex-col w-full">
        {modules.map((module, index) => {
        const isEven = index % 2 === 0;
        const moduleIntegrations = integrations.filter(i => i.moduleId === module.id);
        return <ModuleRow key={module.id} module={module} moduleIntegrations={moduleIntegrations} isEven={isEven} index={index} />;
      })}
      </div>
    </section>;
}